import { AfterViewInit, Component, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DialogRef } from '../../services/modal.service';
import { MapComponent } from '../../components/map/map.component';
import { Parcel } from '../../models/parcel.model';
import { ParcelBoundary } from '../../models/fieldview-boundary.model';
import { ParcelData } from '../../models/fieldview-parcel.model';
import { Point, Polygon, Geometry } from '../../models/geometry.model';
import { Centroid } from '../../models/fieldview-boundary.model';
import { FieldviewService } from '../../services/fieldview.service';
import { Subject } from 'rxjs';
import { Subscription } from 'rxjs';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';

export class FieldviewDialogContext {
  fieldviewParcel: Parcel;
  campaignId: number;
  showImport: boolean;
  showEdit: boolean;
  showClose: boolean;
  isBlocking: boolean;
  size: string;
  backdrop: string;
  configObservable = new Subject<Parcel>();

  emitBoundary() {
    this.configObservable.next(this.fieldviewParcel);
  }
}

@Component({
  standalone: true,
  selector: 'fieldview-dlg',
  styleUrls: ['fieldview-dialog.component.scss'],
  templateUrl: 'fieldview-dialog.component.html',
  providers: [FieldviewService],
  imports: [MapComponent, TranslatePipe, CommonModule],
})
export class FieldViewDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  context: FieldviewDialogContext;
  firstPage: boolean = true;
  lastPage: boolean = false;
  private nextPageToken: string = '';
  private tokenHistory: { index: number; token: string }[] = [];
  private pageIndex: number = -1;
  private subscriptions: Subscription[] = [];
  parcel: Parcel;
  parcelDataList: ParcelData[];
  loading: boolean = true;

  constructor(
    public dialog: DialogRef<FieldviewDialogContext>,
    private fieldViewService: FieldviewService,
    public router: Router,
    private _elementRef: ElementRef,
  ) {
    this.context = dialog.context;
  }

  ngOnInit() {
    this.getFieldViewData('next');
  }

  ngAfterViewInit() {}

  ngOnDestroy() {
    // Unsubscribe from all subscriptions to avoid memory leaks
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  close() {
    this.dialog.close();
  }

  editParcel() {
    this.router.navigate(['/parcels', 'edit', this.parcel.id]);
    this.dialog.close();
  }

  beforeDismiss(): boolean {
    return false;
  }

  beforeClose(): boolean {
    return false;
  }

  getFieldViewData(pagDirection: string) {
    let page = '';
    if (pagDirection == 'prev') {
      this.pageIndex = this.pageIndex - 2;
      page = this.getPrevPageToken();
    }
    if (pagDirection == 'next') {
      if (this.nextPageToken) {
        page = this.nextPageToken;
      }
    }
    this.fieldViewService.getFieldsUser(10, page).subscribe(
      ({ data, nextToken }: { data: ParcelData[]; nextToken: string }) => {
        this.loading = false; // Set loading to false when data is loaded
        // Store X-Next-Token in a variable
        if (data) {
          if (pagDirection == 'next' && this.pageIndex >= 0) {
            this.firstPage = false;
          } else if (pagDirection == 'prev') {
            this.lastPage = false;
          }
          this.pageIndex = this.pageIndex + 1;
          this.addToTokenHistory(nextToken);
          this.parcelDataList = data;
          this.nextPageToken = nextToken;
        } else if (pagDirection == 'next') {
          this.lastPage = true;
        }
      },
      (error) => {
        // Handle error here
        console.error('Error fetching fields:', error);
        this.loading = false; // Set loading to false even if there's an error
      },
    );
  }

  nextPage() {
    this.loading = true;
    this.getFieldViewData('next');
  }

  previousPage() {
    this.loading = true;
    this.getFieldViewData('prev');
  }

  private getPrevPageToken(): string {
    if (this.pageIndex >= 0) {
      return this.tokenHistory[this.pageIndex].token;
    }
    this.tokenHistory = [];
    this.pageIndex = -1;
    this.firstPage = true;
    return null;
  }

  private addToTokenHistory(token: string) {
    this.tokenHistory = [...this.tokenHistory.slice(0, this.pageIndex), { index: this.pageIndex, token }];
  }

  importFlied(fieldId: string) {
    this.getFieldBoundary(fieldId).then(() => {
      this.close();
    });
  }

  getFieldBoundary(fieldId: string) {
    return new Promise((resolve, reject) => {
      const parcelData = this.filterParcelDataById(fieldId);
      const subscription = this.fieldViewService.getFieldBoundary(parcelData.boundaryId).subscribe(
        (result: ParcelBoundary) => {
          this.context.fieldviewParcel = this.createFieldviewParcel(result, parcelData);
          this.context.emitBoundary();
          resolve(result);
        },
        (error) => {
          console.error('Error fetching field boundary:', error);
          reject(error);
        },
      );

      // Store the subscription so that it can be unsubscribed later
      this.subscriptions.push(subscription);
    });
  }

  private filterParcelDataById(fieldId: string): ParcelData {
    return this.parcelDataList.find((parcelData) => parcelData.id === fieldId);
  }

  private createFieldviewParcel(parcelBoundary: ParcelBoundary, parcelData: ParcelData): Parcel {
    const fieldviewParcel = new Parcel();
    fieldviewParcel.name = parcelData.name;
    fieldviewParcel.area = this.convertHectaresToSquareMeters(parcelBoundary.properties.area.q);
    if (parcelBoundary && parcelBoundary.geometry) {
      fieldviewParcel.geometry = this.mapBoundaryGeometry(parcelBoundary.geometry);
      fieldviewParcel.location = this.mapCentroid(parcelBoundary.properties.centroid);
    } else {
      console.error('Invalid ParcelBoundary:', parcelBoundary);
    }
    return fieldviewParcel;
  }

  private convertHectaresToSquareMeters(hectares: number): number {
    const squareMetersPerHectare = 10000;
    const squareMeters = hectares * squareMetersPerHectare;
    return squareMeters;
  }

  private mapBoundaryGeometry(boundaryGeometry: Geometry): any {
    if (boundaryGeometry && boundaryGeometry.type) {
      if (boundaryGeometry.type === 'Polygon' && (boundaryGeometry as Polygon).coordinates) {
        return {
          type: 'Polygon',
          coordinates: (boundaryGeometry as Polygon).coordinates,
        };
      } else if (boundaryGeometry.type === 'Point' && (boundaryGeometry as Point).coordinates) {
        return {
          type: 'Point',
          coordinates: (boundaryGeometry as Point).coordinates,
        };
      } else {
        // Handle other geometry types if needed
        console.error('Unsupported geometry type:', boundaryGeometry.type);
        return null;
      }
    } else {
      console.error('Invalid boundary geometry:', boundaryGeometry);
      return null;
    }
  }

  private mapCentroid(centroid: Centroid): Point {
    if (centroid && centroid.type === 'Point' && centroid.coordinates) {
      return {
        type: 'Point',
        coordinates: centroid.coordinates,
      };
    } else {
      console.error('Invalid centroid:', centroid);
      // You might want to return a default Point or throw an error here
      return { type: 'Point', coordinates: [0, 0] };
    }
  }
}
